import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useRotatingContent from 'dpl/shared/hooks/useRotatingContent';
import SmartImage from 'dpl/components/SmartImage';
import { isMobileUA } from 'dpl/shared/utils';
import Icon from 'dpl/common/components/Icon';

const LOGO_WIDTH = isMobileUA() ? '64px' : '140px';
export default function ContentQuoteCarousel({ title, quotes }) {
  const {
    isOnFirstRotatingContentStep,
    isOnLastRotatingContentStep,
    nextRotatingContent,
    pauseRotatingContent,
    prevRotatingContent,
    resumeRotatingContent,
    ref,
    shownRotatingContentIdx
  } = useRotatingContent({ rotatingContentCount: quotes.length });
  return (
    <div
      className="ContentQuoteCarousel pt12 pb5 pt16-md pb16-md overflow-hidden bg-blue-500 white"
      ref={ref}
      onMouseEnter={() => pauseRotatingContent()}
      onMouseLeave={resumeRotatingContent}
    >
      <div className="container">
        <div className="row slim-gutters-mobile">
          <div className="col-lg-10 col-xl-9 center tc">
            {title && (
              <p className="f5 f8-lg tl tc-ns fw-medium mb8 mb12-lg">{title}</p>
            )}
            <ul className="flex items-center tl tc-ns bb b--white-30 bn-ns mb4 mb0-md mb0-lg">
              {quotes.map((quote, idx) => (
                <li
                  className={classnames(
                    'ContentQuoteCarousel__quote transition',
                    {
                      'o-0': idx !== shownRotatingContentIdx
                    }
                  )}
                  style={{
                    transform: `translateX(${shownRotatingContentIdx * -100}%)`
                  }}
                  key={idx}
                >
                  <div className="d-block d-md-flex justify-center align-items-center mb4 mb8-lg mb6-sm">
                    <p className="ContentQuoteCarousel__quoteSymbol">&ldquo;</p>
                    <p className="fw-medium-l f5-lg ph3-lg">
                      {quote.long_description}
                    </p>
                    <p className="ContentQuoteCarousel__quoteSymbol tc-m tr">
                      &rdquo;
                    </p>
                  </div>
                  <div className="mb8 d-flex d-sm-block flex-column">
                    {quote.title && (
                      <p className="ContentQuoteCarousel__quoteTitle mb4 mb8-sm ml4 ml0-md">{`- ${quote.title}`}</p>
                    )}
                    {quote.photographs[0] && (
                      <div className="ContentQuoteCarousel__quotePhoto">
                        <SmartImage
                          Tag="div"
                          alt={quote.photographs[0].alt_text}
                          className="br-100 overflow-hidden bg-center cover"
                          src={quote.photographs[0].url}
                          width={LOGO_WIDTH}
                          height={LOGO_WIDTH}
                        />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <div className="inline-flex align-items-center">
              <button
                className={classnames({ 'o-30': isOnFirstRotatingContentStep })}
                disabled={isOnFirstRotatingContentStep}
                onClick={prevRotatingContent}
                type="button"
              >
                <Icon name="fat-arrow-left" />
              </button>
              <span className="ph3 f2">
                {shownRotatingContentIdx + 1} / {quotes.length}
              </span>
              <button
                className={classnames({ 'o-30': isOnLastRotatingContentStep })}
                disabled={isOnLastRotatingContentStep}
                onClick={nextRotatingContent}
                type="button"
              >
                <Icon name="fat-arrow-right" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ContentQuoteCarousel.propTypes = {
  title: PropTypes.string,
  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      long_description: PropTypes.string,
      title: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      photographs: PropTypes.array
    })
  ).isRequired
};

ContentQuoteCarousel.defaultProps = {
  title: null
};
